import { AlertTriangle, ArrowLeft, Check, ChevronRight, Copy } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { sourceHref } from "@/components/Belief";
import { DeveloperModeOnly } from "@/components/DeveloperModeOnly";
import {
  ApiError,
  type PipelineStep,
  type ReasonerExchangeView,
  type SynthesisBatchCandidateSubject,
  type SynthesisBatchDetail,
  type SynthesisBatchInsightView,
  type SynthesisBatchVerdictView,
} from "@/lib/api";
import { useApp } from "@/lib/AppContext";
import { cn } from "@/lib/cn";
import { formatCost, formatDate, formatElapsed, formatTime } from "@/lib/format";
import { categoryLabel } from "@/lib/insightCategory";
import { useBatchStepArtifacts } from "@/lib/useStepArtifacts";
import { useDeveloperMode } from "@/lib/uiMode";
import { VERDICT_COLOUR } from "@/lib/verdicts";
import { StepDetails } from "@/pages/StepDetail";
import { StatusPill } from "@/pages/Recordings";

const SUMMARY = "What this batch read, what the model said, and what became a belief.";

/** UI-24 — one synthesis batch, in full. */
export function ProcessingBatchPage() {
  const developerMode = useDeveloperMode();
  const { id } = useParams<{ id: string }>();
  const { api } = useApp();

  const [detail, setDetail] = useState<SynthesisBatchDetail | null | undefined>(undefined);
  const [problem, setProblem] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;

    try {
      const loaded = await api.getSynthesisBatch(id);
      setDetail(loaded);
      setProblem(null);
    } catch (cause: unknown) {
      if (cause instanceof ApiError && cause.status === 404) {
        setDetail(null);
        return;
      }
      setProblem(cause instanceof ApiError ? cause.message : "The batch could not be read.");
    }
  }, [api, id]);

  useEffect(() => {
    if (!developerMode || !id) return;
    setDetail(undefined);
    void load();
  }, [developerMode, id, load]);

  const running = detail && (detail.runStatus === "queued" || detail.runStatus === "processing");

  useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => void load(), 2000);
    return () => clearInterval(timer);
  }, [running, load]);

  if (!developerMode) return <DeveloperModeOnly label="Insight processing" summary={SUMMARY} />;
  if (!id) return null;

  return (
    <div className="flex flex-col gap-[var(--space-6)] px-[var(--space-8)] py-[var(--space-8)]">
      <Link
        to="/processing"
        className="inline-flex w-fit items-center gap-[var(--space-2)] text-[length:var(--text-sm)] text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
      >
        <ArrowLeft size={14} strokeWidth={2} />
        Insight processing
      </Link>

      {problem && (
        <p role="alert" className="text-[length:var(--text-sm)] text-[var(--warning)]">
          {problem}
        </p>
      )}

      {detail === null && (
        <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">
          This batch does not exist, or it is not yours.
        </p>
      )}

      {detail === undefined && !problem && (
        <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">Loading…</p>
      )}

      {detail && <BatchDetail detail={detail} batchId={id} />}
    </div>
  );
}

function BatchDetail({ detail, batchId }: { detail: SynthesisBatchDetail; batchId: string }) {
  const [copied, setCopied] = useState(false);

  async function copyBundle() {
    try {
      await navigator.clipboard.writeText(JSON.stringify(detail, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // A browser that refuses the clipboard leaves the button inert, which is the part
      // that matters — the data is still on screen to select by hand.
    }
  }

  const elapsed =
    detail.completedAt && detail.runStatus !== "queued" && detail.runStatus !== "processing"
      ? Date.parse(detail.completedAt) - Date.parse(detail.createdAt)
      : null;

  const verdictsByInsight = useMemo(() => {
    const map = new Map<string, SynthesisBatchVerdictView[]>();
    for (const verdict of detail.verdicts) {
      const list = map.get(verdict.insightId) ?? [];
      list.push(verdict);
      map.set(verdict.insightId, list);
    }
    return map;
  }, [detail.verdicts]);

  return (
    <div className="flex flex-col gap-[var(--space-6)]">
      {/* -------------------------------------------------------------- header */}
      <section className="rounded-[var(--radius-xl)] border border-[var(--border-subtle)] p-[var(--space-6)]">
        <div className="flex flex-wrap items-center gap-[var(--space-3)]">
          <h1 className="text-[length:var(--text-2xl)] font-bold text-[var(--text-primary)]">
            Batch {formatDate(detail.createdAt)}
          </h1>
          <StatusPill status={detail.runStatus} />
          <BatchStatusBadge status={detail.batchStatus} />
          {detail.runStatus === "processing" && (
            <span
              className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-[var(--radius-full)]"
              style={{ background: "var(--vl-bridge)" }}
              aria-hidden
            />
          )}
        </div>

        <dl className="mt-[var(--space-4)] grid grid-cols-2 gap-x-[var(--space-6)] gap-y-2 text-[length:var(--text-sm)] sm:grid-cols-4">
          <Fact label="Id" value={<Identifier value={batchId} />} />
          <Fact label="Engine" value={detail.engine || "—"} />
          <Fact label="Prompt version" value={detail.promptVersion ?? "—"} />
          <Fact label="Cost" value={formatCost(detail.costUsd) || "—"} />
          <Fact label="Started" value={formatTime(detail.createdAt)} />
          <Fact label="Completed" value={detail.completedAt ? formatTime(detail.completedAt) : "still running"} />
          <Fact label="Elapsed" value={elapsed !== null && elapsed >= 0 ? formatElapsed(elapsed) : "—"} />
          <Fact
            label="Commit"
            value={
              detail.commitSha ? (
                <code className="font-[family-name:var(--font-mono)] text-[length:var(--text-xs)]">
                  {detail.commitSha.slice(0, 7)}
                </code>
              ) : (
                "not published"
              )
            }
          />
        </dl>

        <button
          type="button"
          onClick={() => void copyBundle()}
          className="mt-[var(--space-4)] inline-flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-1.5 text-[length:var(--text-xs)] font-medium text-[var(--text-secondary)] transition hover:bg-[var(--surface-2)]"
        >
          {copied ? <Check size={12} strokeWidth={2} /> : <Copy size={12} strokeWidth={2} />}
          {copied ? "Copied" : "Copy debug bundle"}
        </button>
      </section>

      {/* -------------------------------------------------------------- funnel */}
      <Funnel detail={detail} />

      {/* -------------------------------------------------------------- steps */}
      <section className="flex flex-col gap-[var(--space-2)]">
        <h2 className="text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]">Steps</h2>
        <ol className="flex flex-col gap-[var(--space-2)]">
          {detail.steps.map((step) => (
            <StepRow key={step.id} step={step} batchId={batchId} />
          ))}
        </ol>
      </section>

      {/* -------------------------------------------------------------- insights */}
      <section className="flex flex-col gap-[var(--space-2)]">
        <h2 className="text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]">
          Insights read ({detail.insights.length})
        </h2>
        <ul className="flex flex-col gap-[var(--space-2)]">
          {detail.insights.map((insight) => (
            <InsightRow key={insight.id} insight={insight} verdicts={verdictsByInsight.get(insight.id) ?? []} />
          ))}
          {detail.insights.length === 0 && <Empty text="No insights were read — this pass was over an empty batch." />}
        </ul>
      </section>

      {/* -------------------------------------------------------------- verdicts */}
      <section className="flex flex-col gap-[var(--space-2)]">
        <h2 className="text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]">
          Verdicts applied ({detail.verdicts.length})
        </h2>
        <ul className="flex flex-col gap-[var(--space-2)]">
          {detail.verdicts.map((verdict) => (
            <VerdictRow key={verdict.verdictId} verdict={verdict} />
          ))}
          {detail.verdicts.length === 0 && <Empty text="Nothing was applied to the world view from this batch." />}
        </ul>
      </section>

      {/* -------------------------------------------------------------- dropped */}
      <section className="flex flex-col gap-[var(--space-2)]">
        <h2 className="text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]">
          Not applied ({detail.dropped.length})
        </h2>
        {detail.dropped.length === 0 ? (
          <Empty text="Everything the model proposed was applied." />
        ) : (
          <ul className="flex flex-col gap-[var(--space-2)]">
            {detail.dropped.map((drop, i) => (
              <li
                key={i}
                className="rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-[var(--space-3)] text-[length:var(--text-sm)]"
              >
                <span className="font-semibold text-[var(--warning)]">{DROP_REASON[drop.reason] ?? drop.reason}</span>
                {drop.verdict && (
                  <span className="ml-2 text-[var(--text-secondary)]">
                    — proposed <span className="font-mono">{drop.verdict}</span>
                    {drop.subject && <> on “{drop.subject}”</>}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* -------------------------------------------------------------- candidate pool */}
      <section className="flex flex-col gap-[var(--space-2)]">
        <h2 className="text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]">
          Candidate pool ({detail.candidatePool.length})
        </h2>
        <p className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
          Every subject the model was shown, with the beliefs standing on it before this pass ran.
        </p>
        <ul className="flex flex-col gap-[var(--space-2)]">
          {detail.candidatePool.map((subject) => (
            <CandidateRow key={subject.slug} subject={subject} />
          ))}
          {detail.candidatePool.length === 0 && <Empty text="No subject in the world view matched this batch's insights." />}
        </ul>
      </section>

      {/* -------------------------------------------------------------- downstream */}
      {(detail.relations.length > 0 || detail.commitments.length > 0) && (
        <section className="flex flex-col gap-[var(--space-2)]">
          <h2 className="text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]">Downstream</h2>
          <ul className="flex flex-col gap-[var(--space-2)] text-[length:var(--text-sm)]">
            {detail.relations.map((relation) => (
              <li
                key={relation.id}
                className="rounded-[var(--radius-lg)] border border-[var(--border-subtle)] p-[var(--space-3)]"
              >
                <span style={{ color: VERDICT_COLOUR[relation.verdict] }}>{relation.verdict}</span>{" "}
                <span className="text-[var(--text-primary)]">{relation.subjectName}</span>{" "}
                <span className="text-[var(--text-secondary)]">{relation.type.replace(/_/g, " ")}</span>{" "}
                <span className="text-[var(--text-primary)]">{relation.objectName}</span>
              </li>
            ))}
            {detail.commitments.map((commitment) => (
              <li
                key={commitment.id}
                className="rounded-[var(--radius-lg)] border border-[var(--border-subtle)] p-[var(--space-3)]"
              >
                <span className="text-[var(--text-muted)]">
                  {commitment.direction === "owed_by_me" ? "Owed by you" : "Owed to you"}:
                </span>{" "}
                <span className="text-[var(--text-primary)]">{commitment.statement}</span>
                {commitment.counterpartyName && (
                  <span className="text-[var(--text-secondary)]"> — {commitment.counterpartyName}</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

const DROP_REASON: Record<string, string> = {
  unknown_verdict: "Not one of the six verdict words",
  unknown_insight: "Cited an insight outside the batch",
  unknown_belief: "Named a belief that was never offered",
  no_subject: "Named no subject at all",
  no_statement: "Needed a belief statement and had none",
};

function Funnel({ detail }: { detail: SynthesisBatchDetail }) {
  const stages: { label: string; value: number; warn?: boolean }[] = [
    { label: "Insights read", value: detail.insightsRead },
    { label: "Verdicts proposed", value: detail.proposed },
    { label: "Kept by the parser", value: detail.keptByParser, warn: detail.keptByParser < detail.proposed },
    { label: "Applied", value: detail.applied, warn: detail.applied < detail.keptByParser },
  ];

  return (
    <section className="flex flex-wrap items-center gap-[var(--space-2)] rounded-[var(--radius-xl)] border border-[var(--border-subtle)] p-[var(--space-4)]">
      {stages.map((stage, i) => (
        <div key={stage.label} className="flex items-center gap-[var(--space-2)]">
          {i > 0 && <ChevronRight size={14} strokeWidth={2} className="text-[var(--text-muted)]" aria-hidden />}
          <div className="flex flex-col items-center">
            <span
              className={cn(
                "text-[length:var(--text-lg)] font-bold tabular-nums",
                stage.warn ? "text-[var(--warning)]" : "text-[var(--text-primary)]",
              )}
            >
              {stage.value}
            </span>
            <span className="text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
              {stage.label}
            </span>
          </div>
        </div>
      ))}

      {Object.keys(detail.verdictCounts).length > 0 && (
        <div className="ml-auto flex flex-wrap gap-[var(--space-3)] text-[length:var(--text-xs)]">
          {Object.entries(detail.verdictCounts).map(([verdict, count]) => (
            <span key={verdict} className="flex items-center gap-1">
              <span
                className="h-1.5 w-1.5 rounded-[var(--radius-full)]"
                style={{ background: VERDICT_COLOUR[verdict as keyof typeof VERDICT_COLOUR] ?? "var(--text-muted)" }}
                aria-hidden
              />
              {count} {verdict}
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

function BatchStatusBadge({ status }: { status: SynthesisBatchDetail["batchStatus"] }) {
  const colour = status === "failed" ? "var(--destructive)" : status === "running" ? "var(--vl-bridge)" : "var(--success)";
  return (
    <span
      className="rounded-[var(--radius-full)] px-2.5 py-1 text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)]"
      style={{
        color: colour,
        background: `color-mix(in oklch, ${colour} 16%, transparent)`,
      }}
    >
      batch: {status}
    </span>
  );
}

function StepRow({ step, batchId }: { step: PipelineStep; batchId: string }) {
  const [open, setOpen] = useState(false);
  const { artifacts, problem } = useBatchStepArtifacts(batchId, step.id, open);
  const [exchangesOpen, setExchangesOpen] = useState(false);

  const elapsed =
    step.startedAt && step.completedAt ? Date.parse(step.completedAt) - Date.parse(step.startedAt) : null;

  return (
    <li className="rounded-[var(--radius-lg)] border border-[var(--border-subtle)]">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        className="flex w-full items-center gap-[var(--space-3)] px-[var(--space-4)] py-[var(--space-3)] text-left"
      >
        <ChevronRight
          size={14}
          strokeWidth={2}
          aria-hidden
          className={cn("shrink-0 text-[var(--text-muted)] transition-transform", open && "rotate-90")}
        />
        <span className="min-w-0 flex-1 truncate font-[family-name:var(--font-mono)] text-[length:var(--text-sm)] text-[var(--text-primary)]">
          {step.stepName}
        </span>
        <span className="shrink-0 text-[length:var(--text-xs)] text-[var(--text-secondary)]">
          {[
            step.status,
            elapsed !== null && elapsed >= 0 ? formatElapsed(elapsed) : null,
            step.claimedByName,
            step.model,
            step.costUsd !== null ? formatCost(step.costUsd) : null,
            step.attempts > 1 ? `attempt ${step.attempts}` : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </button>

      {open && (
        <>
          <StepDetails step={step} artifacts={artifacts} problem={problem} />
          <div className="border-t border-[var(--border-subtle)] px-[var(--space-4)] py-[var(--space-3)]">
            <button
              type="button"
              onClick={() => setExchangesOpen((was) => !was)}
              className="text-[length:var(--text-xs)] font-medium text-[var(--text-secondary)] underline decoration-dotted hover:text-[var(--text-primary)]"
            >
              {exchangesOpen ? "Hide model exchange" : "Show model exchange (owner/admin)"}
            </button>
            {exchangesOpen && <ExchangePanel batchId={batchId} stepId={step.id} />}
          </div>
        </>
      )}
    </li>
  );
}

function ExchangePanel({ batchId, stepId }: { batchId: string; stepId: string }) {
  const { api } = useApp();
  const [exchanges, setExchanges] = useState<ReasonerExchangeView[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    let cancelled = false;

    void api
      .getBatchStepExchanges(batchId, stepId)
      .then((loaded) => !cancelled && setExchanges(loaded))
      .catch((cause: unknown) => {
        if (cancelled) return;
        if (cause instanceof ApiError && cause.status === 403) {
          setForbidden(true);
          return;
        }
        setProblem(cause instanceof ApiError ? cause.message : "The model exchange could not be read.");
      });

    return () => {
      cancelled = true;
    };
  }, [api, batchId, stepId]);

  if (forbidden) {
    return (
      <p className="mt-[var(--space-2)] text-[length:var(--text-xs)] text-[var(--text-muted)]">
        This is an owner or admin's own view of what the organization said — ask one of them to open it.
      </p>
    );
  }

  if (problem) {
    return (
      <p role="alert" className="mt-[var(--space-2)] text-[length:var(--text-xs)] text-[var(--warning)]">
        {problem}
      </p>
    );
  }

  if (exchanges === null) {
    return <p className="mt-[var(--space-2)] text-[length:var(--text-xs)] text-[var(--text-secondary)]">Loading…</p>;
  }

  if (exchanges.length === 0) {
    return (
      <p className="mt-[var(--space-2)] text-[length:var(--text-xs)] text-[var(--text-secondary)]">
        No model call is recorded for this step — it predates this table, or it never called a model.
      </p>
    );
  }

  return (
    <div className="mt-[var(--space-2)] flex flex-col gap-[var(--space-3)]">
      {exchanges.map((exchange) => (
        <div key={exchange.id} className="rounded-[var(--radius-md)] bg-[var(--surface-1)] p-[var(--space-3)]">
          <p className="mb-[var(--space-2)] flex flex-wrap items-center gap-[var(--space-2)] text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
            {formatTime(exchange.occurredAt)} · {exchange.placement} · {exchange.model}
            {exchange.wasCutOff && (
              <span className="flex items-center gap-1 normal-case text-[var(--destructive)]">
                <AlertTriangle size={11} strokeWidth={2} /> cut off at the ceiling
              </span>
            )}
            {exchange.error && <span className="normal-case text-[var(--destructive)]">{exchange.error}</span>}
            {(exchange.inputTokens !== null || exchange.outputTokens !== null) && (
              <span className="normal-case">
                {exchange.inputTokens ?? "?"} in · {exchange.outputTokens ?? "?"} out
                {exchange.durationMs !== null && ` · ${formatElapsed(exchange.durationMs)}`}
              </span>
            )}
          </p>
          <ExchangeBody label="System prompt" text={exchange.systemPrompt} />
          <ExchangeBody label="User prompt" text={exchange.userPrompt} />
          <ExchangeBody label="Response" text={exchange.response} truncated={exchange.responseTruncated} />
          {exchange.thinking && <ExchangeBody label="Thinking" text={exchange.thinking} />}
        </div>
      ))}
    </div>
  );
}

function ExchangeBody({ label, text, truncated }: { label: string; text: string | null; truncated?: boolean }) {
  if (text === null) return null;

  return (
    <details className="mt-[var(--space-2)]">
      <summary className="cursor-pointer text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
        {label}
      </summary>
      <pre className="mt-[var(--space-1)] max-h-64 overflow-auto whitespace-pre-wrap break-all font-[family-name:var(--font-mono)] text-[length:var(--text-2xs)] leading-relaxed text-[var(--text-secondary)]">
        {text}
      </pre>
      {truncated && (
        <p className="mt-[var(--space-1)] text-[length:var(--text-2xs)] text-[var(--text-muted)]">
          Shown up to the preview limit — the stored call goes on past this.
        </p>
      )}
    </details>
  );
}

function InsightRow({ insight, verdicts }: { insight: SynthesisBatchInsightView; verdicts: SynthesisBatchVerdictView[] }) {
  return (
    <li className="rounded-[var(--radius-lg)] border border-[var(--border-subtle)] p-[var(--space-3)]">
      <div className="flex flex-wrap items-center gap-[var(--space-2)]">
        <span className="text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
          {categoryLabel(insight.category)}
        </span>
        {insight.subject && (
          <span className="text-[length:var(--text-2xs)] text-[var(--text-muted)]">about {insight.subject}</span>
        )}
        <Link
          to={sourceHref(insight.sourceKind, insight.sourceMaterialId, insight.id)}
          className="ml-auto text-[length:var(--text-xs)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:underline"
        >
          {insight.sourceTitle ?? "Untitled source"}
        </Link>
      </div>
      <p className="mt-1 text-[length:var(--text-sm)] text-[var(--text-primary)] [text-wrap:pretty]">{insight.text}</p>
      <p className="mt-1 flex flex-wrap gap-[var(--space-2)] text-[length:var(--text-xs)]">
        {verdicts.length === 0 ? (
          <span className="text-[var(--text-muted)]">no change</span>
        ) : (
          verdicts.map((verdict) => (
            <span key={verdict.verdictId} className="flex items-center gap-1">
              <span
                className="h-1.5 w-1.5 rounded-[var(--radius-full)]"
                style={{ background: VERDICT_COLOUR[verdict.verdict] }}
                aria-hidden
              />
              <span style={{ color: VERDICT_COLOUR[verdict.verdict] }}>{verdict.verdict}</span>
              <span className="text-[var(--text-muted)]">on {verdict.subjectTitle}</span>
            </span>
          ))
        )}
      </p>
    </li>
  );
}

function VerdictRow({ verdict }: { verdict: SynthesisBatchVerdictView }) {
  return (
    <li className="rounded-[var(--radius-lg)] border border-[var(--border-subtle)] p-[var(--space-3)]">
      <div className="flex flex-wrap items-center gap-[var(--space-2)] text-[length:var(--text-xs)]">
        <span
          className="rounded-[var(--radius-full)] px-2 py-0.5 font-semibold"
          style={{
            color: VERDICT_COLOUR[verdict.verdict],
            background: `color-mix(in oklch, ${VERDICT_COLOUR[verdict.verdict]} 16%, transparent)`,
          }}
        >
          {verdict.verdict}
        </span>
        <Link to={`/world-view/${verdict.subjectSlug}`} className="font-medium text-[var(--text-primary)] hover:underline">
          {verdict.subjectTitle}
        </Link>
        {verdict.assertedByUser && <span className="text-[var(--text-muted)]">asserted by you</span>}
        <span className="ml-auto tabular-nums text-[var(--text-secondary)]">
          {verdict.confidenceBefore !== null ? verdict.confidenceBefore.toFixed(2) : "new"}
          {" → "}
          {verdict.confidenceNow !== null ? verdict.confidenceNow.toFixed(2) : "—"}
        </span>
      </div>

      <p className="mt-1 text-[length:var(--text-sm)] text-[var(--text-primary)] [text-wrap:pretty]">{verdict.statement}</p>

      {verdict.reasoning && (
        <p className="mt-1 text-[length:var(--text-xs)] italic text-[var(--text-muted)] [text-wrap:pretty]">
          {verdict.reasoning}
        </p>
      )}

      {verdict.supersededStatement && (
        <p className="mt-1 text-[length:var(--text-xs)] text-[var(--text-muted)]">
          Closed: <span className="line-through">{verdict.supersededStatement}</span>
        </p>
      )}

      {verdict.contradictionId && (
        <p className="mt-1 text-[length:var(--text-xs)] text-[var(--destructive)]">
          Filed as a contradiction ({verdict.contradictionStatus}) —{" "}
          <Link to="/world-view?kind=contradictions" className="underline">
            see the queue
          </Link>
        </p>
      )}
    </li>
  );
}

function CandidateRow({ subject }: { subject: SynthesisBatchCandidateSubject }) {
  return (
    <li
      className={cn(
        "rounded-[var(--radius-lg)] border p-[var(--space-3)]",
        subject.touched ? "border-[var(--border-default)]" : "border-[var(--border-subtle)] opacity-70",
      )}
    >
      <div className="flex items-center gap-[var(--space-2)]">
        <Link to={`/world-view/${subject.slug}`} className="font-medium text-[var(--text-primary)] hover:underline">
          {subject.title}
        </Link>
        <span className="text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
          {subject.kind}
        </span>
        {subject.touched && (
          <span className="ml-auto text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--success)]">
            touched
          </span>
        )}
      </div>
      {subject.beliefs.length === 0 ? (
        <p className="mt-1 text-[length:var(--text-xs)] text-[var(--text-muted)]">No current beliefs held yet.</p>
      ) : (
        <ul className="mt-1 flex flex-col gap-0.5 text-[length:var(--text-xs)] text-[var(--text-secondary)]">
          {subject.beliefs.map((belief) => (
            <li key={belief.id}>
              {belief.statement}
              {belief.confidence !== null && (
                <span className="ml-1 tabular-nums text-[var(--text-muted)]">({belief.confidence.toFixed(2)})</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
        {label}
      </dt>
      <dd className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">{value}</dd>
    </div>
  );
}

function Identifier({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // The value is already shown; a browser that refuses the clipboard changes nothing else.
    }
  }

  return (
    <span className="flex items-center gap-[var(--space-2)]">
      <code className="truncate font-[family-name:var(--font-mono)] text-[length:var(--text-xs)]">{value}</code>
      <button type="button" onClick={() => void copy()} aria-label="Copy id" className="text-[var(--text-muted)] hover:text-[var(--text-primary)]">
        {copied ? <Check size={12} strokeWidth={2} /> : <Copy size={12} strokeWidth={2} />}
      </button>
    </span>
  );
}

function Empty({ text }: { text: string }) {
  return <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">{text}</p>;
}
